import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IListingImageCompressionPreview,
  IPartImageSettings,
  IPartImageSettingsResponse,
} from "../models/PartImageSettings";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";

export default function usePartImageSettings() {
  const { get, post, postGetFile } = useApiHelper();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ["partImageSettings"],
    queryFn: () => get<IPartImageSettingsResponse>("/partimagesettings"),
  });

  const createOrUpdate = useMutation(
    (settings: IPartImageSettings) => post<IPartImageSettingsResponse>("/partimagesettings", settings as any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["partImageSettings"]);
      },
    }
  );

  const apply = useMutation(
    (partIds: string[]) => post<DefaultResponse>("/partimagesettings/apply", partIds as any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["partImageSettings"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
        queryClient.invalidateQueries(["part"]);
      },
    }
  );

  const getPreview = async (settings: IPartImageSettings): Promise<string> => {
    const blob = await postGetFile<IPartImageSettings>("/partimagesettings/preview", settings as any);
    return URL.createObjectURL(blob);
  };

  // Nothing is written when previewing, so it is safe to call on every slider change.
  const getCompressionPreview = useMutation(
    ({ maxDimension, quality }: { maxDimension: number; quality: number }) =>
      post<
        { maxDimension: number; quality: number },
        DefaultResponseWithData<IListingImageCompressionPreview>
      >("/partimagesettings/compression-preview", { maxDimension, quality })
  );

  return {
    settings: settings.data?.data,
    loading: settings.isLoading,
    createOrUpdate,
    apply,
    getPreview,
    getCompressionPreview,
  };
}
